import { motion } from "framer-motion";
import { FileWarning, Phone, Users, MapPin, ArrowUpRight, CheckCircle2, Calendar, FolderCheck } from "lucide-react";
import { Badge } from "../../../components/ui/Badge";
import Tooltip from "../../../components/ui/Tooltip";

function formatCurrency(val) {
  if (!val || isNaN(val)) return "—";
  const num = Number(val);
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1).replace(".", ",")} M€`;
  }
  if (num >= 1_000) {
    return `${Math.round(num / 1_000)} k€`;
  }
  return `${num.toLocaleString("fr-FR")} €`;
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return null;
  }
}

export default function FamilleCard({ family, onClick }) {
  const auditDateFormatted = formatDate(family.dateAudit);
  const initials = (family.nomFamille?.charAt(0) || family.nom?.charAt(0) || "F").toUpperCase();
  
  // Référence BDD : UUID réel (ex: 7c564bd5-3347-426c-8eb5-0db45698df75) ou externalReference
  const fullRef = family.reference || family.id || "";
  const displayRef = fullRef.length >= 32 && fullRef.includes("-")
    ? `DOS-${fullRef.slice(0, 8).toUpperCase()}`
    : (fullRef ? `Réf. ${fullRef}` : "DOS-NC");

  return (
    <motion.div
      onClick={onClick}
      role="button"
      tabIndex={0}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.99 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
      className="group relative flex flex-col justify-between h-full p-4 sm:p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm hover:border-[var(--hp-color-accent)] hover:shadow-hp-md transition-all duration-200 cursor-pointer overflow-hidden"
    >
      {/* --- TOP: Avatar Agrandie, Nom Élargi & Badges Rehaussés --- */}
      <div className="flex items-start justify-between gap-3.5">
        <div className="flex items-center gap-3.5 min-w-0">
          {/* Avatar Agrandie avec micro-animation */}
          <motion.div
            whileHover={{ scale: 1.08, rotate: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-[var(--hp-ink-800)] to-[var(--hp-ink-950)] text-white font-extrabold text-2xl sm:text-3xl flex items-center justify-center shadow-hp-md shrink-0 border border-white/10 tracking-wide ring-2 ring-workspace"
          >
            {initials}
          </motion.div>

          <div className="min-w-0 flex-1">
            {/* Nom Agrandi */}
            <h2 className="text-lg sm:text-xl font-bold tracking-tight text-primary group-hover:text-[var(--hp-color-accent-text)] transition-colors truncate">
              {family.nomFamille || family.nom} {family.prenom || ""}
            </h2>

            <div className="flex items-center gap-2 mt-1 text-xs text-subtle">
              <span className="font-semibold text-muted bg-surface-muted px-2 py-0.5 rounded-[var(--hp-radius-sm)] border border-workspace">
                {family.regimeMatrimonial || "Foyer Fiscal"}
              </span>
              {(family.ville || family.codePostal) && (
                <>
                  <span className="text-[var(--hp-color-border-strong)]">•</span>
                  <span className="inline-flex items-center gap-1 truncate text-subtle font-medium">
                    <MapPin className="w-3.5 h-3.5 shrink-0 text-muted" />
                    <span className="truncate">{family.ville || family.codePostal}</span>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Badges Agrandis */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Badge variant="info" className="px-3 py-1 text-xs font-semibold shadow-hp-sm">
            {family.statut || "Client"}
          </Badge>
          {!family.dateAudit ? (
            <Badge variant="warning" className="px-3 py-1 text-xs font-semibold shadow-hp-sm flex items-center gap-1.5">
              <FileWarning className="w-3.5 h-3.5" />
              Audit requis
            </Badge>
          ) : (
            <Badge variant="success" className="px-3 py-1 text-xs font-semibold shadow-hp-sm flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Audit conforme
            </Badge>
          )}
        </div>
      </div>

      {/* --- MIDDLE: Micro-Dashboard KPI Inset --- */}
      <div className="grid grid-cols-3 gap-2.5 my-3 py-2.5 px-3.5 rounded-lg bg-[var(--hp-color-surface-muted)]/60 border border-[var(--hp-color-border-muted)]">
        <div className="flex flex-col min-w-0">
          <span className="text-[10px] font-semibold text-subtle uppercase tracking-wider truncate">
            Patrimoine Net
          </span>
          <span className="text-sm sm:text-base font-bold text-primary truncate mt-0.5">
            {formatCurrency(family.patrimoineNet || family.patrimoineBrut)}
          </span>
        </div>

        <div className="flex flex-col min-w-0 border-l border-[var(--hp-color-border-muted)] pl-3">
          <span className="text-[10px] font-semibold text-subtle uppercase tracking-wider truncate">
            Revenus Ann.
          </span>
          <span className="text-sm sm:text-base font-bold text-primary truncate mt-0.5">
            {family.revenusNetsAnnuels ? formatCurrency(family.revenusNetsAnnuels) : "—"}
          </span>
        </div>

        <div className="flex flex-col min-w-0 border-l border-[var(--hp-color-border-muted)] pl-3">
          <span className="text-[10px] font-semibold text-subtle uppercase tracking-wider truncate">
            Profil Risque
          </span>
          <span className="text-sm sm:text-base font-semibold text-[var(--hp-color-accent-text)] truncate mt-0.5">
            {family.profilInvestisseur || family.toleranceRisque || "Modéré"}
          </span>
        </div>
      </div>

      {/* --- BOTTOM: Coordonnées & Date d'Audit (Sans redondance) --- */}
      <div className="flex items-center justify-between gap-3 text-xs text-subtle pt-0.5">
        <div className="flex items-center gap-3.5 truncate min-w-0">
          <div className="inline-flex items-center gap-1.5 shrink-0 text-muted font-medium">
            <Phone className="w-3.5 h-3.5 shrink-0 text-subtle" />
            <span>{family.telephone || family.phone || family.mobile || "Tél. non renseigné"}</span>
          </div>

          <div className="inline-flex items-center gap-1.5 shrink-0 font-medium text-subtle">
            <Users className="w-3.5 h-3.5 shrink-0 text-subtle" />
            <span>{family.membresFoyer?.length || 1} membre(s)</span>
          </div>
        </div>

        {/* Date de l'audit uniquement si réalisé (élimine la redondance avec "Audit requis") */}
        {auditDateFormatted ? (
          <div className="inline-flex items-center gap-1 shrink-0 text-[11px] font-medium text-muted bg-surface-muted px-2 py-0.5 rounded">
            <Calendar className="w-3 h-3 text-subtle" />
            <span>Audit : {auditDateFormatted}</span>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1 shrink-0 text-[11px] text-subtle">
            <FolderCheck className="w-3 h-3 text-subtle" />
            <span>Foyer actif</span>
          </div>
        )}
      </div>

      {/* --- FOOTER: Référence Dossier BDD & Action --- */}
      <div className="mt-3 pt-2.5 border-t border-workspace flex items-center justify-between text-xs">
        <Tooltip content={`Identifiant BDD complet : ${fullRef}`}>
          <span 
            className="text-[11px] text-subtle font-mono tracking-tight font-medium hover:text-primary transition-colors cursor-help"
          >
            {displayRef}
          </span>
        </Tooltip>
        <div className="inline-flex items-center gap-1 font-semibold text-[var(--hp-color-accent-text)] group-hover:text-[var(--hp-color-accent-active)] transition-colors">
          <span>Consulter le dossier</span>
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </motion.div>
  );
}
